import React, { useState, useCallback, useEffect, useMemo } from 'react';
import moment from 'moment';
import withNavigation from '../higher-order/navigation';
import { useSeoTitle } from '../../utils/hooks';
import { formatMontant } from '../../utils/formatting';
import Translator from '../translator';
import ProfileLabeledInput from '../profile-labeled-input';
import { getMesReservations, getUniteEquipmentType } from '../../server/services/reservation-service';
import LoadingSpinner from '../svg/loading';
import './mes-reservations.css';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

function normalizeGuidKey(id) {
  return String(id || '').trim().toLowerCase();
}

function getSommaireEquipmentRows(som) {
  const list = som?.reservationDetailEquipementsList;
  if (Array.isArray(list) && list.length > 0) {
    return list;
  }
  const single = som?.reservationDetailEquipements;
  if (single && typeof single === 'object') {
    return [single];
  }
  return [];
}

async function populateUniteEquipmentTypeLabels(data, noneText) {
  const soms = data?.reservationDetailSommaires;
  if (!Array.isArray(soms)) {
    return data;
  }
  const cache = new Map();
  for (const som of soms) {
    const rows = getSommaireEquipmentRows(som);
    for (const eq of rows) {
      const typeIdKey = normalizeGuidKey(eq?.idUniteEquipementType);
      if (!typeIdKey || typeIdKey === EMPTY_GUID) {
        eq.uniteEquipmentTypeLabel = noneText || '—';
        continue;
      }
      if (cache.has(typeIdKey)) {
        eq.uniteEquipmentTypeLabel = cache.get(typeIdKey);
        continue;
      }
      const typeRes = await getUniteEquipmentType(typeIdKey);
      let label = '';
      if (typeRes?.ok) {
        const row = await typeRes.json();
        label = row && typeof row.description === 'string' ? row.description.trim() : '';
      }
      const finalLabel = label || eq?.idUniteEquipementType || '—';
      cache.set(typeIdKey, finalLabel);
      eq.uniteEquipmentTypeLabel = finalLabel;
    }
  }
  return { ...data };
}

function MesReservationsPage(props) {
  const language = props.match?.params?.language || 'fr';
  const TEXTS = useMemo(
    () => Translator(language, 'TXT_MES_RESERVATIONS_PAGE') || {},
    [language]
  );

  useSeoTitle(TEXTS.seoTitle || TEXTS.title || '');

  const [noConfirmation, setNoConfirmation] = useState('');
  const [email, setEmail] = useState('');
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(props.location?.search || '');
    const qNo = (params.get('noConfirmation') || params.get('no') || '').trim();
    const qEmail = (params.get('email') || '').trim();
    if (qNo) {
      setNoConfirmation(qNo);
    }
    if (qEmail) {
      setEmail(qEmail);
    }
  }, [props.location?.search]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setReservation(null);
    if (!(noConfirmation || '').trim() || !(email || '').trim()) {
      setError(TEXTS.errorBothRequired || '');
      return;
    }
    setLoading(true);
    try {
      const trimmedNo = (noConfirmation || '').trim();
      const trimmedEmail = (email || '').trim();
      const res = await getMesReservations(trimmedNo, trimmedEmail);
      if (!res?.ok) throw new Error(`Erreur ${res?.status ?? ''}`.trim());
      const data = await res.json();
      const newReservation = await populateUniteEquipmentTypeLabels(
        data,
        TEXTS.equipmentTypeNone || '—'
      );
      setReservation(newReservation);
      const params = new URLSearchParams(props.location?.search || '');
      params.set('noConfirmation', trimmedNo);
      params.set('email', trimmedEmail);
      params.delete('no');
      const qs = params.toString();
      props.history.replace({
        pathname: props.location.pathname,
        search: qs ? `?${qs}` : ''
      });
    } catch (err) {
      setReservation(null);
      setError((err && err.message) || TEXTS.errorGeneric || '');
    } finally {
      setLoading(false);
    }
  }, [noConfirmation, email, TEXTS, props.history, props.location.pathname, props.location.search]);

  const handleReset = useCallback(() => {
    setNoConfirmation('');
    setEmail('');
    setReservation(null);
    setError('');
    const params = new URLSearchParams(props.location?.search || '');
    params.delete('no');
    params.delete('noConfirmation');
    params.delete('email');
    const qs = params.toString();
    props.history.replace({
      pathname: props.location.pathname,
      search: qs ? `?${qs}` : ''
    });
  }, [props.history, props.location.pathname, props.location.search]);

  const entete = reservation?.reservationEntete;
  const sommaires = Array.isArray(reservation?.reservationDetailSommaires) ? reservation.reservationDetailSommaires : [];
  const paiements = Array.isArray(reservation?.paiements) ? reservation.paiements : [];

  return (
    <div id="profile-page" className="mes-reservations-root">
      <div
        className="profile-tab-panel profile-tab-panel--empty mes-reservations__panel"
        style={{ display: 'flow-root' }}
      >
        <div className="profile-historique-achats-tab mes-reservations__inner">
          <h2 className="profile-historique-achats-title">
            <span className="profile-span">{TEXTS.title}</span>
          </h2>
          <p className="profile-historique-achats-message profile-historique-achats-message--muted">
            {TEXTS.subtitle}
          </p>

          <form onSubmit={handleSubmit}>
            <ProfileLabeledInput label={TEXTS.noLabel} value={noConfirmation} onChange={(ev) => setNoConfirmation(ev.target.value)} />
            <ProfileLabeledInput label={TEXTS.emailLabel} type="email" value={email} onChange={(ev) => setEmail(ev.target.value)} />
            {error && (
              <p className="profile-historique-achats-message profile-historique-achats-message--error" role="alert">
                {error}
              </p>
            )}
            <div className="profile-div mes-reservations__form-actions">
              <button type="submit" className="btn-login mes-reservations__submit" disabled={loading}>
                <span className="profile-span">{loading ? TEXTS.loading : TEXTS.submit}</span>
              </button>
              <button
                type="button"
                className="mes-reservations__btn-secondary"
                onClick={handleReset}
                disabled={loading}
              >
                {TEXTS.reset}
              </button>

            </div>
          </form>

          {loading && (
            <div className="mes-reservations__loading-wrap" aria-live="polite" aria-busy="true">
              <LoadingSpinner background="transparent" height="80px" />
            </div>
          )}

          {reservation && entete ? (
            <>
              <div className="profile-div profile-separator" />

              <h3 className="profile-depots-sous-titre">
                <span className="profile-span">#{entete.noConfirmation || noConfirmation}</span>
              </h3>

              <div className="profile-depots-summary">
                <div className="profile-depots-summary-row">
                  <span className="profile-span profile-depots-summary-label">{TEXTS.dates}</span>
                </div>
                <div className="profile-depots-summary-row mes-reservations__summary-dates-row">
                  <div className="mes-reservations__summary-date-half">
                    <span className="profile-span profile-depots-summary-label">{TEXTS.from}</span>
                    <span className="profile-span profile-depots-summary-value">
                      {reservation.dateArrivee ? moment(reservation.dateArrivee).format('LLL') : '—'}
                    </span>
                  </div>
                  <div className="mes-reservations__summary-date-half">
                    <span className="profile-span profile-depots-summary-label">{TEXTS.to}</span>
                    <span className="profile-span profile-depots-summary-value">
                      {reservation.dateDepart ? moment(reservation.dateDepart).format('LLL') : '—'}
                    </span>
                  </div>
                </div>
                <div className="profile-depots-summary-row">
                  <span className="profile-span profile-depots-summary-label">{TEXTS.guests}</span>
                </div>
                <div className="profile-depots-summary-row mes-reservations__summary-guests-row">
                  <div className="mes-reservations__summary-guest-half">
                    <span className="profile-span profile-depots-summary-label">{TEXTS.adults}</span>
                    <span className="profile-span profile-depots-summary-value">
                      {entete.nombreAdultes ?? '—'}
                    </span>
                  </div>
                  <div className="mes-reservations__summary-guest-half">
                    <span className="profile-span profile-depots-summary-label">{TEXTS.children}</span>
                    <span className="profile-span profile-depots-summary-value">
                      {entete.nombreEnfants ?? '—'}
                    </span>
                  </div>
                </div>
              </div>

              <h3 className="profile-depots-sous-titre">
                <span className="profile-span">{TEXTS.amounts}</span>
              </h3>
              <div className="profile-depots-summary">
                <div className="profile-depots-summary-row mes-reservations__summary-amounts-row">
                  <div className="mes-reservations__summary-amount-half">
                    <span className="profile-span profile-depots-summary-label">{TEXTS.subtotal}</span>
                    <span className="profile-span profile-depots-summary-value">
                      {formatMontant(reservation.sousTotal, language)}
                    </span>
                  </div>
                  <div className="mes-reservations__summary-amount-half">
                    <span className="profile-span profile-depots-summary-label">{TEXTS.total}</span>
                    <span className="profile-span profile-depots-summary-value">
                      {formatMontant(reservation.total, language)}
                    </span>
                  </div>
                </div>
                <div className="profile-depots-summary-row">
                  <span className="profile-span profile-depots-summary-label">{TEXTS.taxes}</span>
                </div>
                <div className="profile-depots-summary-row mes-reservations__summary-amounts-row">
                  <div className="mes-reservations__summary-amount-half">
                    <span className="profile-span profile-depots-summary-label">{TEXTS.taxTpsShort}</span>
                    <span className="profile-span profile-depots-summary-value">
                      {formatMontant(reservation.tps, language)}
                    </span>
                  </div>
                  <div className="mes-reservations__summary-amount-half">
                    <span className="profile-span profile-depots-summary-label">{TEXTS.taxTvqShort}</span>
                    <span className="profile-span profile-depots-summary-value">
                      {formatMontant(reservation.tvq, language)}
                    </span>
                  </div>
                </div>
                <div className="profile-depots-summary-row">
                  <span className="profile-span profile-depots-summary-label">{TEXTS.lodgingTax}</span>
                </div>
                <div className="profile-depots-summary-row mes-reservations__summary-amounts-row">
                  <div className="mes-reservations__summary-amount-half">
                    <span className="profile-span profile-depots-summary-value">
                      {formatMontant(reservation.taxeHebergement, language)}
                    </span>
                  </div>
                </div>
                <div className="profile-depots-summary-row">
                  <span className="profile-span profile-depots-summary-label">{TEXTS.paid}</span>
                </div>
                <div className="profile-depots-summary-row mes-reservations__summary-amounts-row">
                  <div className="mes-reservations__summary-amount-half">
                    <span className="profile-span profile-depots-summary-value">
                      {formatMontant(reservation.montantPaye, language)}
                    </span>
                  </div>
                </div>
              </div>

              <h3 className="profile-depots-sous-titre">
                <span className="profile-span">{TEXTS.lines}</span>
              </h3>
              {sommaires.length === 0 ? (
                <p className="profile-historique-achats-message profile-historique-achats-message--muted">
                  {TEXTS.emptySommaires}
                </p>
              ) : (
                sommaires.map((som, somIdx) => {
                  const details = Array.isArray(som.reservationDetails) ? som.reservationDetails : [];
                  const equipmentRows = getSommaireEquipmentRows(som);

                  return (
                    <div key={som.idReservationDetailSommaire || `som-${somIdx}`} className="mes-reservations__line-block">
                      <p className="profile-historique-achats-message">
                        <span className="profile-historique-achats-cell-no">{som.typeAuxiliaire || '—'}</span>
                        {' — '}
                        {formatMontant(som.montant, language)}
                        {som.dateDebut && som.dateFin ? (
                          <>
                            {' '}
                            <span className="profile-historique-achats-message--muted">
                              ({moment(som.dateDebut).format('L')} – {moment(som.dateFin).format('L')})
                            </span>
                          </>
                        ) : null}
                      </p>
                      {details.length > 0 ? (
                        <div className="profile-historique-achats-table-wrap mes-reservations__table-wrap--spaced">
                          <table className="profile-historique-achats-table">
                            <thead>
                              <tr>
                                <th scope="col">{TEXTS.lineDates}</th>
                                <th scope="col">{TEXTS.qty}</th>
                                <th scope="col" className="profile-historique-achats-th-numeric">{TEXTS.unitPrice}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {details.map((d) => (
                                <tr key={d.idReservationDetail || `${d.dateDebut}-${d.dateFin}`}>
                                  <td>
                                    {d.dateDebut ? moment(d.dateDebut).format('L') : '—'}
                                    {' → '}
                                    {d.dateFin ? moment(d.dateFin).format('L') : '—'}
                                  </td>
                                  <td>{d.quantite}</td>
                                  <td className="profile-historique-achats-cell-numeric">
                                    {formatMontant(d.prixParJourAdultes, language)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                      {equipmentRows.map((eq, eqIdx) => {
                        const hasEq =
                          eq &&
                          typeof eq === 'object' &&
                          (eq.idUniteEquipementType || eq.longeurEquipement != null || eq.uniteEquipmentTypeLabel);
                        if (!hasEq) {
                          return null;
                        }
                        const typeEquipementLabel = eq.uniteEquipmentTypeLabel ?? '—';
                        return (
                          <div
                            key={eq.idReservationDetailEquipement || eqIdx}
                            className="profile-depots-summary mes-reservations__equip-summary"
                          >
                            <div className="profile-depots-summary-row">
                              <span className="profile-span profile-depots-summary-label">{TEXTS.equipments}</span>
                            </div>
                            <div className="profile-depots-summary-row">
                              <span className="profile-span profile-depots-summary-label">{TEXTS.length}</span>
                              <span className="profile-span profile-depots-summary-value">
                                {eq.longeurEquipement != null ? eq.longeurEquipement : '—'}
                              </span>
                            </div>
                            <div className="profile-depots-summary-row">
                              <span className="profile-span profile-depots-summary-label">{TEXTS.typeId}</span>
                              <span className="profile-span profile-depots-summary-value">
                                {typeEquipementLabel}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              )}

              <h3 className="profile-depots-sous-titre">
                <span className="profile-span">{TEXTS.payments}</span>
              </h3>
              {paiements.length === 0 ? (
                <p className="profile-historique-achats-message profile-historique-achats-message--muted">
                  {TEXTS.noPayments}
                </p>
              ) : (
                <div className="profile-historique-achats-table-wrap">
                  <table className="profile-historique-achats-table">
                    <thead>
                      <tr>
                        <th scope="col">{TEXTS.paymentDate}</th>
                        <th scope="col" className="profile-historique-achats-th-numeric">{TEXTS.paymentAmount}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paiements.map((p, pIdx) => (
                        <tr key={p.idPaiement || p.id_paiement || `pay-${pIdx}`}>
                          <td>{p.datePaiement ? moment(p.datePaiement).format('LLL') : '—'}</td>
                          <td className="profile-historique-achats-cell-numeric">
                            {formatMontant(p.montant, language)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default withNavigation(MesReservationsPage);
